// Estimate preparation extracted from factor-pipeline.ts so computeFactors
// stays focused on factor arithmetic. This module owns the analyst-estimate
// filtering, manual-override application, NTM (next-twelve-months) EPS
// blending for non-calendar fiscal years, and the year-2 fallback.

import type { StockData, StockOverride } from './types';
import type { AnalystEstimate } from './fmp';

/**
 * Returns the prepared `est` array — already filtered to fiscal years ending
 * >3 months from now, sorted ascending so est[0] = next FY, est[1] = the one
 * after, with manual Bloomberg/FactSet overrides applied and the NTM
 * blend done for short-window FY ends. The 9%-fallback for year-2 also
 * lives here.
 */
export function prepareEstimates(d: StockData, override?: StockOverride): AnalystEstimate[] {
  // Filter estimates to fiscal years ending >3 months from now (not mostly elapsed),
  // then sort ascending so est[0] = next FY ending, est[1] = the one after.
  // Handles non-December fiscal year-ends (e.g. AAPL Sep, NVDA Jan).
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() + 3);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  const est = [...(d.estimates || [])]
    .filter((e) => e.date > cutoffStr)
    .sort((a, b) => a.date.localeCompare(b.date));

  // Apply manual overrides (Bloomberg/FactSet consensus) if provided
  if (override) {
    if (override.fwdEps != null) {
      if (est.length > 0) est[0].epsAvg = override.fwdEps;
      else est.push({ date: `${new Date().getFullYear() + 1}-12-31`, epsAvg: override.fwdEps, epsHigh: override.fwdEps, epsLow: override.fwdEps, revenueAvg: 0, revenueHigh: 0, revenueLow: 0, numberAnalystsEstimatedEps: 0, numberAnalystsEstimatedRevenue: 0 } as unknown as AnalystEstimate);
    }
    if (override.fwdEps2yr != null) {
      if (est.length > 1) est[1].epsAvg = override.fwdEps2yr;
      else if (est.length === 1) est.push({ date: `${new Date().getFullYear() + 2}-12-31`, epsAvg: override.fwdEps2yr, epsHigh: override.fwdEps2yr, epsLow: override.fwdEps2yr, revenueAvg: 0, revenueHigh: 0, revenueLow: 0, numberAnalystsEstimatedEps: 0, numberAnalystsEstimatedRevenue: 0 } as unknown as AnalystEstimate);
    }
    if (override.fwdRev != null) {
      if (est.length > 0) est[0].revenueAvg = override.fwdRev;
    }
    if (override.fwdRev2yr != null) {
      if (est.length > 1) est[1].revenueAvg = override.fwdRev2yr;
    }
  }

  // NTM (Next Twelve Months) EPS blending — fixes non-calendar fiscal year distortion.
  // For companies with FY ending in <12 months, the 1BF estimate covers less than 12 months.
  // Blend 1BF and 2BF proportionally to always represent a true 12-month forward window.
  // Formula: ntm_eps = (months_to_fy/12) * eps_1bf + (1 - months_to_fy/12) * eps_2bf
  // Example: MSFT (June FY, 3 months away): ntm = 0.25 * 1BF + 0.75 * 2BF
  const monthsToFY = (() => {
    if (override?.fyEndMonth == null) return override?.monthsToNextFY ?? null;
    const now = new Date();
    const fyMonth = override.fyEndMonth; // 1=Jan, 12=Dec
    let nextFYEnd = new Date(now.getFullYear(), fyMonth, 0); // last day of FY end month
    if (nextFYEnd <= now) nextFYEnd = new Date(now.getFullYear() + 1, fyMonth, 0);
    const diffMs = nextFYEnd.getTime() - now.getTime();
    return diffMs / (30.44 * 24 * 60 * 60 * 1000); // approximate months
  })();
  if (monthsToFY != null && monthsToFY < 11.5 && est.length >= 2) {
    const w1 = Math.max(0, Math.min(1, monthsToFY / 12));
    const w2 = 1 - w1;
    if (est[0].epsAvg && est[1].epsAvg) {
      est[0].epsAvg = w1 * est[0].epsAvg + w2 * est[1].epsAvg;
    }
    if (est[0].revenueAvg && est[1].revenueAvg) {
      est[0].revenueAvg = w1 * est[0].revenueAvg + w2 * est[1].revenueAvg;
    }
  }

  // Fallback: if year-1 estimates exist but year-2 don't, assume 9% growth
  if (est.length === 1) {
    const e1 = est[0];
    est.push({
      date: `${parseInt(e1.date.slice(0, 4)) + 1}-12-31`,
      epsAvg: e1.epsAvg > 0 ? e1.epsAvg * 1.09 : e1.epsAvg,
      epsHigh: e1.epsHigh > 0 ? e1.epsHigh * 1.09 : e1.epsHigh,
      epsLow: e1.epsLow > 0 ? e1.epsLow * 1.09 : e1.epsLow,
      revenueAvg: e1.revenueAvg > 0 ? e1.revenueAvg * 1.09 : e1.revenueAvg,
      revenueHigh: e1.revenueHigh > 0 ? e1.revenueHigh * 1.09 : e1.revenueHigh,
      revenueLow: e1.revenueLow > 0 ? e1.revenueLow * 1.09 : e1.revenueLow,
    } as unknown as AnalystEstimate);
  }

  return est;
}
